"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getTextToolCopy } from "@/lib/text-tool-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function DiffChecker() {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [differences, setDifferences] = useState<{line: number, type: string, content: string}[]>([]);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getTextToolCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "diff-checker") ?? tools[0],
    locale
  );

  const checkDiff = () => {
    const lines1 = text1.split("\n");
    const lines2 = text2.split("\n");
    const maxLines = Math.max(lines1.length, lines2.length);
    const diffs: {line: number, type: string, content: string}[] = [];

    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || "";
      const line2 = lines2[i] || "";

      if (line1 !== line2) {
        if (line1 && !line2) {
          diffs.push({ line: i + 1, type: "removed", content: line1 });
        } else if (!line1 && line2) {
          diffs.push({ line: i + 1, type: "added", content: line2 });
        } else {
          diffs.push({ line: i + 1, type: "modified", content: `- ${line1}\n+ ${line2}` });
        }
      }
    }

    setDifferences(diffs);
  };

  return (

    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
           <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:compare" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.originalText}</label>
          <textarea
            value={text1}
            onChange={(e) => setText1(e.target.value)}
            placeholder={copy.enterOriginalText}
            className="min-h-[300px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 font-mono text-sm text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
          />
        </div>

        <div>
           <label className="mb-3 block text-lg font-medium text-positivus-dark">{copy.changedText}</label>
          <textarea
            value={text2}
            onChange={(e) => setText2(e.target.value)}
            placeholder={copy.enterChangedText}
            className="min-h-[300px] w-full rounded-[20px] border-2 border-positivus-dark/20 bg-white p-5 font-mono text-sm text-positivus-dark placeholder:text-gray-400 focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
          />
        </div>
      </div>

      <button
        onClick={checkDiff}
        className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
      >
        {copy.compareTexts}
      </button>

      {differences.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-positivus-dark">
            {copy.differencesFound}: {differences.length}
          </h3>
          <div className="space-y-3 max-h-[500px] overflow-y-auto rounded-[20px] border-2 border-positivus-dark bg-white p-6">
            {differences.map((diff, index) => (
              <div
                key={index}
                className={`rounded-[15px] border-2 p-4 ${
                  diff.type === "added"
                    ? "bg-green-50 border-green-200"
                    : diff.type === "removed"
                    ? "bg-red-50 border-red-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-mono font-bold text-gray-500 uppercase tracking-wider">
                    {copy.line} {diff.line}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded-md font-bold uppercase tracking-wider ${
                      diff.type === "added"
                        ? "bg-green-200 text-green-800"
                        : diff.type === "removed"
                        ? "bg-red-200 text-red-800"
                        : "bg-yellow-200 text-yellow-800"
                    }`}
                  >
                    {diff.type === "added" ? copy.added : diff.type === "removed" ? copy.removed : copy.modified}
                  </span>
                </div>
                <pre className="text-sm font-mono whitespace-pre-wrap text-positivus-dark">{diff.content}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {differences.length === 0 && text1 && text2 && (
        <div className="rounded-[20px] border-2 border-positivus-dark/20 bg-green-50 p-6 text-center">
          <Icon icon="mdi:check-circle" className="mx-auto h-12 w-12 text-green-500 mb-3" />
          <p className="text-lg font-bold text-positivus-dark">{copy.noDifferencesFound}</p>
          <p className="text-gray-500">{copy.textsAreIdentical}</p>
        </div>
      )}
    </div>
  );
}
